import type { RevampedDebtRecordDto } from '../types/debtOverviewApi.types'
import type { DebtItem } from './debtItem'

import { canonicalDebtRecord } from './debtItem'

interface DebtOverviewMixPanelStatistics {
	numberOfDebtRecords: number
	numberOfCompleteDebtRecords: number
	numberOfDeletedDebtRecords: number
	numberOfDebtRecordsWithMissingPayments: number
	numberOfDebtRecordsWithBalanceMissing: number
	numberOfDebtsAdded: number
	numberOfPaymentsAdjusted: number
	numberOfBalancesAdjusted: number
}

export const getMixPanelStatistics = (
	originalDebtEntries: DebtItem[],
	submittedDebtEntries: DebtItem[],
): DebtOverviewMixPanelStatistics => {
	const originalDebtRecords = originalDebtEntries.flatMap(
		(debtEntry) => debtEntry.debtRecords,
	)
	const submittedDebtRecords = submittedDebtEntries.flatMap(
		(debtEntry) => debtEntry.debtRecords,
	)

	return {
		numberOfDebtRecords: originalDebtEntries.length,
		numberOfCompleteDebtRecords:
			getNumberOfCompleteDebtRecords(originalDebtEntries),
		numberOfDeletedDebtRecords: getNumberOfDeletedDebtRecords(
			originalDebtRecords,
			submittedDebtEntries,
		),
		numberOfDebtRecordsWithMissingPayments:
			getNumberOfDebtRecordsWithMissingPayments(originalDebtEntries),
		numberOfDebtRecordsWithBalanceMissing:
			getNumberOfDebtRecordsWithBalanceMissing(originalDebtEntries),
		numberOfDebtsAdded: getNumberOfDebtsAdded(
			originalDebtRecords,
			submittedDebtRecords,
		),
		numberOfPaymentsAdjusted: getNumberOfPaymentsAdjusted(
			originalDebtRecords,
			submittedDebtRecords,
		),
		numberOfBalancesAdjusted: getNumberOfBalancesAdjusted(
			originalDebtEntries,
			submittedDebtEntries,
		),
	}
}

const getAllDebtRecords = (debtEntries: DebtItem[]) =>
	debtEntries.flatMap((debtEntry) => debtEntry.debtRecords)

const getNumberOfDeletedDebtRecords = (
	originalDebtRecords: RevampedDebtRecordDto[],
	submittedDebtEntries: DebtItem[],
) =>
	originalDebtRecords.filter((originalDebtRecord) =>
		submittedDebtEntries.some(
			(x) =>
				canonicalDebtRecord(x).id === originalDebtRecord.id &&
				canonicalDebtRecord(x).status !== 'active',
		),
	).length

const getNumberOfDebtRecordsWithMissingPayments = (debtEntries: DebtItem[]) =>
	debtEntries.filter(
		(debtEntry) => debtEntry.debtPayment.paymentAmount == undefined,
	).length

const getNumberOfDebtRecordsWithBalanceMissing = (debtEntries: DebtItem[]) =>
	getAllDebtRecords(debtEntries).filter(
		(debtRecord) => debtRecord.principalAmount == undefined,
	).length

const getNumberOfDebtsAdded = (
	originalDebtRecords: RevampedDebtRecordDto[],
	submittedDebtRecords: RevampedDebtRecordDto[],
) =>
	submittedDebtRecords.filter(
		(x) => !originalDebtRecords.some((dr) => dr.id === x.id),
	).length

const getNumberOfPaymentsAdjusted = (
	originalDebtRecords: RevampedDebtRecordDto[],
	submittedDebtRecords: RevampedDebtRecordDto[],
) =>
	submittedDebtRecords.filter((submittedDebtRecord) => {
		const originalDebtRecord = originalDebtRecords.find(
			(x) => x.id === submittedDebtRecord.id,
		)
		return (
			originalDebtRecord != undefined &&
			originalDebtRecord.principalAmount !== submittedDebtRecord.principalAmount
		)
	}).length

const getNumberOfBalancesAdjusted = (
	originalDebtEntries: DebtItem[],
	submittedDebtEntries: DebtItem[],
) =>
	submittedDebtEntries.filter((submittedDebtEntry) => {
		const originalDebtEntry = originalDebtEntries.find(
			(originalDebtEntry) => originalDebtEntry.id === submittedDebtEntry.id,
		)
		return (
			originalDebtEntry &&
			originalDebtEntry.debtPayment.paymentAmount !==
				submittedDebtEntry.debtPayment.paymentAmount
		)
	}).length

const getNumberOfCompleteDebtRecords = (debtEntries: DebtItem[]) =>
	debtEntries.filter(
		(debtEntry) =>
			debtEntry.debtRecords.every(
				(debtRecord) => debtRecord.principalAmount != undefined,
			) && debtEntry.debtPayment.paymentAmount != undefined,
	).length
